import React, { FunctionComponent, ReactElement, useState } from 'react';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useLocation } from 'react-router-dom';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Groups } from '../groups.enum';
import { useAuth } from '../hooks/useAuth';

interface NavigationProps {
  label?: string;
  children: any;
}

export const Navigation = ({
  label,
  children,
}: NavigationProps): ReactElement => (
  <ul
    className="nav nav-pills nav-sidebar flex-column"
    data-widget="treeview"
    role="menu"
    data-accordion="false"
  >
    {label && <li className="nav-header">{label}</li>}
    {children}
  </ul>
);

interface NavigationItemProps {
  label: string;
  icon?: IconProp;
  path?: string;
  permissions?: string[];
  index?: number;
  groups?: Groups[];
}

export const NavigationItem: FunctionComponent<NavigationItemProps> = ({
  groups,
  path,
  label,
  children,
  icon,
}) => {
  const [openItems, setOpenItems] = useState<string[]>([]);
  const auth = useAuth();
  const location = useLocation();

  // Check permissions
  if (
    !auth.payload?.group ||
    (groups && !groups.includes(auth.payload['group']))
  )
    return <span />;

  const toggleItem = (name: string) => {
    return (): void => {
      const newItems = [...openItems];
      if (openItems.indexOf(name) > -1) {
        newItems.splice(newItems.indexOf(name), 1);
      } else {
        newItems.push(name);
      }

      setOpenItems(newItems);
    };
  };

  const itemKey = `${path}-${label}`;
  const isActive = path && location.pathname.indexOf(path) > -1;
  const aChildIsActive =
    children &&
    React.Children.map(
      children as ReactElement,
      (child) =>
        child.props.path && location.pathname.indexOf(child.props.path) > -1
    ).filter((val: boolean) => val).length > 0;

  return (
    <li
      className={`nav-item has-treeview ${
        openItems.indexOf(itemKey) > -1 || isActive || aChildIsActive
          ? 'menu-open active'
          : ''
      } ${isActive ? 'active' : ''}`}
    >
      {/* eslint-disable-next-line no-nested-ternary */}
      {!children ? (
        path?.startsWith('http://') || path?.startsWith('https://') ? (
          <a
            href={path}
            className="nav-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <small>
              <FontAwesomeIcon icon="share" />
            </small>
            {icon && <FontAwesomeIcon icon={icon} className="mr-2" />}
            <p> {label}</p>
          </a>
        ) : (
          <Link to={path || ''} className="nav-link">
            {icon && <FontAwesomeIcon icon={icon} className="mr-2" />}
            <p>{label}</p>
          </Link>
        )
      ) : (
        <>
          <button type="button" onClick={toggleItem(itemKey)}>
            {icon && <FontAwesomeIcon icon={icon} className="mr-2" />}

            {label}
            <FontAwesomeIcon icon={faAngleLeft} className="right" />
          </button>
          <ul className="nav nav-treeview">
            {React.Children.map(
              children as ReactElement[],
              (child) =>
                child &&
                child.props.path && (
                  <li key={child.props.path} className="nav-item">
                    <Link to={child.props.path} className="nav-link">
                      <FontAwesomeIcon
                        icon={child.props.icon}
                        className="mr-2"
                      />
                      {child.props.label}
                    </Link>
                  </li>
                )
            )}
          </ul>
        </>
      )}
    </li>
  );
};
